import { Alert, StatusType } from '@platform/witems';
import {
  isLabsReviewTask, labHoldPhrase, labNoteOf, labNoteShareOf, labReleaseOf, labReleaseWhen, labsResultDate, labsResultOf,
  type LabNote, type LabNoteShare, type LabRelease, type TaskLike,
} from '../logic/labs';
import { ELATION_PATIENT_BASE_URL } from '../config/medical-constants';

function releaseLine(release: LabRelease): { type: StatusType.INFO | StatusType.GOOD | StatusType.MODERATE; text: string } {
  if (release.state === 'released') {
    const when = labReleaseWhen(release.at);
    const shared = when ? `Shared with the patient on ${when}` : 'Shared with the patient';
    if (!release.withheld.length) return { type: StatusType.GOOD, text: shared };
    const n = release.withheld.length;
    return {
      type: StatusType.MODERATE,
      text: `${shared}. Left off the card: ${release.withheld.join(', ')} (the lab sent two different values${n === 1 ? '' : ' for each'}). Go over ${n === 1 ? 'it' : 'them'} with the patient.`,
    };
  }
  if (release.stale) return { type: StatusType.MODERATE, text: 'Not confirmed as shared with the patient' };
  if (release.state === 'requested') return { type: StatusType.INFO, text: 'Sharing with the patient…' };
  if (release.state === 'held') {
    const why = labHoldPhrase(release.reason);
    return { type: StatusType.MODERATE, text: why ? `Held: ${why}` : 'Held' };
  }
  // 'window' is the expiry itself, not a cause worth repeating.
  const why = release.reason === 'window' ? null : labHoldPhrase(release.reason);
  return { type: StatusType.MODERATE, text: why ? `Not shared with the patient (held 72 h: ${why})` : 'Not shared with the patient (72 h passed)' };
}

const SHARE_LINE: Record<LabNoteShare, string> = {
  sent: 'Sent with the results card',
  pending: 'Not sent yet',
  not_sent: 'Not sent',
};

// The care team's note as recorded: the full name for the team, and only what the task proves about sharing.
function NoteStatus({ note, share }: { note: LabNote; share: LabNoteShare }) {
  return (
    <div className="lab-note-status">
      <span className="detail1 text-secondary">
        {['Note for the patient', note.author, SHARE_LINE[share]].filter(Boolean).join(' · ')}
      </span>
      <p className="body2 lab-note-status__text">{note.text}</p>
    </div>
  );
}

// Mounted on BOTH task surfaces (drawer + patient-file card) so they can never disagree about
// whether a patient's baseline labs have come back. No stamp at all renders nothing: WF72 may be
// switched off, and a "waiting on results" line would claim an automation that isn't running.
//
// The verb follows the stamp's provenance. 'elation' means Elation dated the results, so the strip
// can say they were filed; 'detected' means the order was fulfilled with no date attached, so it
// says only that we saw them. Rendering a detection time as a filing date is a claim we cannot
// prove — and this is the surface a coordinator reads before opening the chart.
export default function LabResultsStatus({ task, ehrPatientId }: {
  task: TaskLike | null | undefined;
  ehrPatientId?: string | number | null;
}) {
  const note = labNoteOf(task);
  const noteBlock = note ? <NoteStatus note={note} share={labNoteShareOf(task, note)} /> : null;
  // Once the review is Done the question is no longer "are results in" but "did the patient get them".
  const release = labReleaseOf(task);
  if (release) {
    const { type, text } = releaseLine(release);
    return (
      <div style={{ margin: '8px 0 0' }}>
        <Alert type={type} text={text} />
        {noteBlock}
      </div>
    );
  }
  // A note recorded while sharing was off still shows, with a share line that claims nothing.
  if (!isLabsReviewTask(task)) return noteBlock;
  const result = labsResultOf(task);
  if (!result) return noteBlock;

  const when = labsResultDate(result);
  const verb = result.datedFrom === 'elation' ? 'filed' : 'seen';
  const dated = when ? ` — ${verb} ${when}` : '';
  const chartLink = ehrPatientId
    ? {
      linkText: 'Open in Elation',
      linkURL: `${ELATION_PATIENT_BASE_URL}/${ehrPatientId}/`,
      // Elation is a different system: same-tab navigation loses the drawer and the queue position.
      linkInNewTab: true,
    }
    : {};

  // ⚠️ The two states must NOT look alike. A panel with the blood back and the urine outstanding is
  // not "results are in", and the green all-done tone on an incomplete panel is what would make a
  // coordinator close the review task before the last specimen lands — the measured cause of uACR
  // sitting at 4% of the roster. So: different tone, different verb, and the COUNT leads.
  const partial = result.state === 'partial';
  const { gridsFinal: done, gridsTotal: total } = result;
  const counted = done != null && total != null ? `${done} of ${total} panels back` : 'Some results are back';

  // Name the outstanding specimens only when the list is short. Measured live: a patient with 2 of 7
  // panels back yields 31 test names — unreadable, and it buries the count, which is the part that
  // separates "nearly done" from "barely started".
  const names = result.pendingTests;
  const outstanding = total != null && done != null ? total - done : 0;
  let stillOut = '';
  if (names.length > 0 && names.length <= 5) {
    stillOut = ` Still waiting on: ${names.join(', ').toLowerCase()}.`;
  } else if (outstanding > 0) {
    stillOut = ` ${outstanding} panel${outstanding === 1 ? '' : 's'} still pending.`;
  }

  const text = partial
    ? `${counted}${dated}.${stillOut} Review what's back in Elation; this task stays open.`
    : `Lab results are in the Elation chart${dated}. Review them there, then complete this task.`;

  return (
    <div style={{ margin: '8px 0 0' }}>
      <Alert
        type={partial ? StatusType.MODERATE : StatusType.GOOD}
        text={text}
        {...chartLink}
      />
      {noteBlock}
    </div>
  );
}
